import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button, Input } from '@/shared-ui';

interface Props {
  problem: string;
  email: string;
  problemError?: string;
  emailError?: string;
  onProblemChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onGoBack: () => void;
  onContinue: () => void;
}

export default function ProjectUsabilityHandsfreeStuckFormStep({
  problem,
  email,
  problemError,
  emailError,
  onProblemChange,
  onEmailChange,
  onGoBack,
  onContinue,
}: Props) {
  const t = useTranslations('UserTest');

  return (
    <div className='flex flex-col gap-4 p-6' data-test='handsfree-stuck-form'>
      <h2 className='text-base font-semibold text-gray-900'>
        {t('stuck.title')}
      </h2>

      <div className='flex flex-col gap-2'>
        <label htmlFor='problem-input' className='text-sm text-gray-900'>
          {t('stuck.problemLabel')}
        </label>
        <Input
          id='problem-input'
          type='text'
          placeholder={t('stuck.problemPlaceholder')}
          value={problem}
          onChange={(e) => onProblemChange(e.target.value)}
          className={problemError ? 'border-red-500' : 'border-gray-200'}
          data-test='handsfree-stuck-problem-input'
          aria-label={t('stuck.problemAriaLabel')}
          aria-invalid={Boolean(problemError)}
          aria-describedby={problemError ? 'problem-error' : undefined}
        />
        {problemError && (
          <p
            id='problem-error'
            className='text-sm text-red-600'
            role='alert'
            data-test='handsfree-stuck-problem-error'
          >
            {problemError}
          </p>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        <label htmlFor='email-input' className='text-sm text-gray-900'>
          {t('stuck.emailLabel')}
        </label>
        <Input
          id='email-input'
          type='email'
          placeholder={t('stuck.emailPlaceholder')}
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          className={emailError ? 'border-red-500' : 'border-gray-200'}
          data-test='handsfree-stuck-email-input'
          aria-label={t('stuck.emailAriaLabel')}
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? 'email-error' : undefined}
        />
        {emailError && (
          <p
            id='email-error'
            className='text-sm text-red-600'
            role='alert'
            data-test='handsfree-stuck-email-error'
          >
            {emailError}
          </p>
        )}
      </div>

      <div className='h-px w-full bg-gray-200' aria-hidden='true' />

      <div className='flex items-center justify-between'>
        <Button
          onClick={onGoBack}
          variant='secondary'
          size='md'
          className='rounded-full bg-gray-100 hover:bg-gray-200'
          data-test='handsfree-stuck-go-back-button'
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className='size-3'
            aria-hidden='true'
          />
          {t('stuck.goBack')}
        </Button>
        <Button
          onClick={onContinue}
          variant='primary'
          size='md'
          className='rounded-full bg-gray-900 shadow-lg hover:bg-gray-800'
          data-test='handsfree-stuck-continue-button'
        >
          {t('stuck.continue')}
        </Button>
      </div>
    </div>
  );
}
